// src/pages/blog/BlogPost.jsx
// Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { createPortal } from 'react-dom';
import { blogService } from '../../services/blogService';
import { useTheme } from '../../contexts/ThemeContext';
import CommentSection from '../../components/admin/blog/CommentSection';
import BlogPostSkeleton from '../../components/admin/blog/BlogPostSkeleton';
import { FiverrCard } from '../../components/FiverrCard';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar,
  FiClock,
  FiEye,
  FiTag,
  FiFolder,
  FiArrowLeft,
  FiShare2,
  FiHeart,
  FiChevronRight,
  FiChevronLeft,
  FiUser,
  FiMessageCircle,
  FiExternalLink,
  FiTrendingUp,
  FiZap,
  FiAward,
  FiGrid,
  FiPackage,
  FiBookmark,
  FiInfo,
  FiArrowUp,
  FiList,
  FiArrowRight,
  FiLink,
  FiTwitter,
  FiFacebook,
  FiLinkedin,
  FiInstagram,
  FiSend,
  FiX,
  FiCopy,
  FiMail,
  FiCheckCircle,
  FiImage,
  FiDownload,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
// ✅ Import simplified ogImageService for share modal ONLY
// REMOVED: generateSocialMetaData, SOCIAL_PLATFORMS from cloudinarySocialService
import { ogImageService } from '../../services/ogImageService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const DEFAULT_IMAGE = '/og-images/default/og-image-default.png';

// ============================================
// READING TIME UTILITIES
// ============================================

const getFormattedReadingTime = minutes => {
  if (!minutes || minutes === 0) return '1 min read';
  if (minutes === 1) return '1 min read';
  return `${minutes} min read`;
};

const getReadingTimeColor = minutes => {
  if (!minutes || minutes <= 0)
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (minutes <= 3) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  } else if (minutes <= 7) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  } else if (minutes <= 12) {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
  } else {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  }
};

// ============================================
// READING PROGRESS BAR
// ============================================

const ReadingProgressBar = React.memo(() => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = (scrollTop / scrollHeight) * 100;
      setProgress(Math.min(scrolled, 100));
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-1 z-50">
      <div
        className="h-full transition-all duration-200"
        style={{ width: `${progress}%`, backgroundColor: 'var(--text)' }}
      />
    </div>
  );
});

// ============================================
// SOCIAL SHARE MODAL - Uses simplified ogImageService
// ============================================

const SocialShareModal = React.memo(({ isOpen, onClose, blog, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${shareUrl}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'blog_post',
          content_id: blog?.id,
          content_title: blog?.title,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  // ✅ Use simplified ogImageService for modal preview ONLY
  const shareData = useMemo(() => {
    if (!blog) return null;
    return ogImageService.blogPost(blog);
  }, [blog]);

  if (!isOpen || !blog || !shareData) return null;

  const sharePlatforms = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: FiTwitter,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(shareUrl)}&via=wdhejbul`,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: FiFacebook,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(blog.title)}`,
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: FiLinkedin,
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: FaWhatsapp,
      color: '#25D366',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`📝 ${blog.title}\n\n${shareUrl}`)}`,
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: FiSend,
      color: '#26A5E4',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`📝 ${blog.title}`)}`,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: FiInstagram,
      color: '#E4405F',
      url: null,
      action: () => {
        copyToClipboard();
        alert('📸 Instagram: Link copied! Open Instagram app to share.');
        trackShare('instagram');
      },
    },
    {
      id: 'email',
      name: 'Email',
      icon: FiMail,
      color: '#EA4335',
      url: `mailto:?subject=${encodeURIComponent(`Check out: ${blog.title}`)}&body=${encodeURIComponent(`I thought you might find this interesting:\n\n${blog.title}\n${shareUrl}`)}`,
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          backgroundColor: isDark
            ? 'rgba(20,20,20,0.98)'
            : 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          border: isDark
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(0,0,0,0.06)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 px-6 py-4 border-b flex items-center justify-between"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            backgroundColor: isDark
              ? 'rgba(20,20,20,0.95)'
              : 'rgba(255,255,255,0.95)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.05)',
              }}
            >
              <FiShare2 className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
            <div>
              <h3
                className="text-lg font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Share this post
              </h3>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Spread the word about "{blog.title}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Preview Card - Uses simplified shareData */}
        <div className="px-6 pt-4">
          <div
            className="rounded-xl overflow-hidden border"
            style={{
              borderColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
            }}
          >
            <div className="relative aspect-[1.91/1] bg-gray-100 dark:bg-gray-800">
              <img
                src={shareData.image || DEFAULT_IMAGE}
                alt={blog.title}
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.src = DEFAULT_IMAGE;
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                <p className="text-white font-semibold text-lg line-clamp-1">
                  {shareData.title || blog.title}
                </p>
                <p className="text-white/70 text-sm line-clamp-1">
                  {shareData.description || blog.excerpt}
                </p>
                <p className="text-white/40 text-xs mt-0.5">{shareUrl}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Share Buttons Grid */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {sharePlatforms.map(platform => {
              const Icon = platform.icon;
              const isDisabled = !platform.url && !platform.action;

              return (
                <button
                  key={platform.id}
                  onClick={() => {
                    if (platform.action) {
                      platform.action();
                    } else if (platform.url) {
                      window.open(
                        platform.url,
                        '_blank',
                        'noopener,noreferrer,width=600,height=400',
                      );
                      trackShare(platform.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:scale-105 hover:shadow-md active:scale-95'
                  }`}
                  style={{
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(0,0,0,0.02)',
                    border: isDark
                      ? '1px solid rgba(255,255,255,0.06)'
                      : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${platform.color}15`,
                      color: platform.color,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    {platform.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Copy Link Section */}
        <div
          className="px-6 pb-6 border-t"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="pt-4">
            <div
              className="flex items-center gap-2 p-2 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <FiLink
                className="w-4 h-4 flex-shrink-0"
                style={{ color: 'var(--muted)' }}
              />
              <p
                className="text-xs truncate flex-1"
                style={{ color: 'var(--muted)' }}
              >
                {shareUrl}
              </p>
              <button
                onClick={copyToClipboard}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  copied
                    ? 'text-green-500 bg-green-500/10'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: copied ? '#22c55e' : 'var(--text)' }}
              >
                {copied ? (
                  <>
                    <FiCheckCircle className="w-3.5 h-3.5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <FiCopy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
});

// ============================================
// SHARE BUTTONS - Updated with Modal
// ============================================

const ShareButtons = React.memo(({ title, url, blogId, blog }) => {
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${url}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'blog_post',
          content_id: blogId,
          content_title: title,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  const quickShare = platform => {
    const encodedTitle = encodeURIComponent(`📝 ${title}`);
    const encodedUrl = encodeURIComponent(url);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&via=wdhejbul`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%0A${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      instagram: null,
      email: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
    };

    if (platform === 'instagram') {
      copyToClipboard();
      alert('📸 Instagram: Link copied! Open Instagram app to share.');
      trackShare('instagram');
      return;
    }

    const shareUrl = urls[platform];
    if (shareUrl) {
      window.open(
        shareUrl,
        '_blank',
        'noopener,noreferrer,width=600,height=400',
      );
      trackShare(platform);
    }
  };

  const quickPlatforms = [
    { id: 'twitter', icon: FiTwitter, color: '#1DA1F2' },
    { id: 'facebook', icon: FiFacebook, color: '#1877F2' },
    { id: 'linkedin', icon: FiLinkedin, color: '#0A66C2' },
    { id: 'whatsapp', icon: FaWhatsapp, color: '#25D366' },
    { id: 'telegram', icon: FiSend, color: '#26A5E4' },
  ];

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          onClick={copyToClipboard}
          className={`p-1.5 rounded-lg transition-all duration-200 cursor-pointer ${
            copied
              ? 'text-green-500 bg-green-500/10'
              : 'hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ color: copied ? '#22c55e' : 'var(--text)' }}
          aria-label="Copy link"
          title="Copy link"
        >
          {copied ? (
            <FiCheckCircle className="w-4 h-4" />
          ) : (
            <FiCopy className="w-4 h-4" />
          )}
        </button>

        {quickPlatforms.map(platform => {
          const Icon = platform.icon;
          return (
            <button
              key={platform.id}
              onClick={() => quickShare(platform.id)}
              className="p-1.5 rounded-lg transition-all duration-200 cursor-pointer hover:scale-110 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              aria-label={`Share on ${platform.id}`}
              title={`Share on ${platform.id}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer text-sm"
          style={{ color: 'var(--text)' }}
          aria-label="More share options"
          title="More share options"
        >
          <FiShare2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>

      {showModal && (
        <SocialShareModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          blog={blog}
          shareUrl={url}
        />
      )}
    </>
  );
});

// ============================================
// TABLE OF CONTENTS
// ============================================

const TableOfContents = React.memo(({ headings }) => {
  const [activeId, setActiveId] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filteredHeadings = useMemo(() => {
    return headings?.filter(h => h.level === 2 || h.level === 3) || [];
  }, [headings]);

  const scrollToHeading = useCallback(id => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      window.history.pushState({}, '', `#${id}`);
      setActiveId(id);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash && filteredHeadings.length) {
      const id = window.location.hash.slice(1);
      const exists = filteredHeadings.some(h => h.id === id);
      if (exists) {
        setTimeout(() => scrollToHeading(id), 500);
      }
    }
  }, [filteredHeadings, scrollToHeading]);

  useEffect(() => {
    if (!filteredHeadings.length) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0% -70% 0%', threshold: 0.1 },
    );

    filteredHeadings.forEach(heading => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [filteredHeadings]);

  if (!filteredHeadings.length) return null;

  return (
    <div
      className="my-6 p-4 rounded-xl border transition-all duration-300 overflow-hidden"
      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg)' }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full font-semibold text-sm"
        style={{ color: 'var(--text)' }}
      >
        <span className="flex items-center gap-2">
          <FiList className="w-4 h-4" />
          Table of Contents
        </span>
        <FiChevronRight
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
        />
      </button>
      {isOpen && (
        <nav
          className="space-y-0.5 max-h-80 overflow-y-auto mt-3 pt-3 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          {filteredHeadings.map((heading, index) => (
            <button
              key={index}
              onClick={() => scrollToHeading(heading.id)}
              className={`block py-1.5 text-xs transition-all w-full text-left rounded-lg px-2 ${
                activeId === heading.id
                  ? 'font-medium bg-black/10 dark:bg-white/10'
                  : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              style={{
                paddingLeft: `${(heading.level - 1) * 16 + 8}px`,
                color: activeId === heading.id ? '#3b82f6' : 'var(--text)',
              }}
            >
              {heading.text}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
});

// ============================================
// BREADCRUMB
// ============================================

const Breadcrumb = React.memo(({ category, title }) => {
  return (
    <nav
      className="flex items-center gap-2 text-xs mb-4 overflow-x-auto"
      style={{ color: 'var(--muted)' }}
      aria-label="Breadcrumb"
    >
      <Link to="/" className="hover:underline">
        Home
      </Link>
      <FiChevronRight className="w-3 h-3 flex-shrink-0" />
      <Link to="/blog" className="hover:underline">
        Blog
      </Link>
      {category && (
        <>
          <FiChevronRight className="w-3 h-3 flex-shrink-0" />
          <Link
            to={`/blog/category/${encodeURIComponent(category)}`}
            className="hover:underline capitalize"
          >
            {category}
          </Link>
        </>
      )}
      <FiChevronRight className="w-3 h-3 flex-shrink-0" />
      <span className="truncate" style={{ color: 'var(--text)' }}>
        {title}
      </span>
    </nav>
  );
});

// ============================================
// POPULAR POSTS COMPONENT
// ============================================

const PopularPosts = React.memo(({ posts }) => {
  if (!posts?.length) return null;

  return (
    <div
      className="p-4 rounded-xl border overflow-hidden"
      style={{ borderColor: 'var(--border)' }}
    >
      <h3
        className="text-sm font-semibold mb-3 flex items-center gap-2"
        style={{ color: 'var(--text)' }}
      >
        <FiTrendingUp className="w-4 h-4 text-orange-500" />
        Popular Posts
      </h3>
      <div className="space-y-3">
        {posts.slice(0, 5).map((post, index) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group flex items-start gap-3 transition-all hover:translate-x-1"
          >
            <span className="text-xs font-bold text-blue-500 min-w-[20px]">
              #{index + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p
                className="text-xs font-medium line-clamp-2 group-hover:underline"
                style={{ color: 'var(--text)' }}
              >
                {post.title}
              </p>
              <div
                className="flex items-center gap-2 text-[10px] mt-0.5"
                style={{ color: 'var(--muted)' }}
              >
                <FiClock className="w-2.5 h-2.5" />
                <span>{getFormattedReadingTime(post.readingTime || 1)}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
});

// ============================================
// CATEGORIES WIDGET
// ============================================

const CategoriesWidget = React.memo(({ categories }) => {
  if (!categories?.length) return null;

  return (
    <div
      className="p-4 rounded-xl border overflow-hidden"
      style={{ borderColor: 'var(--border)' }}
    >
      <h3
        className="text-sm font-semibold mb-3 flex items-center gap-2"
        style={{ color: 'var(--text)' }}
      >
        <FiFolder className="w-4 h-4 text-blue-500" />
        Categories
      </h3>
      <div className="flex flex-wrap gap-2">
        {categories.slice(0, 8).map(cat => (
          <Link
            key={cat.name}
            to={`/blog/category/${encodeURIComponent(cat.name)}`}
            className="px-3 py-1 rounded-full text-xs transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--border-light)',
              color: 'var(--text)',
            }}
          >
            {cat.name}
            <span
              className="ml-1 text-[10px]"
              style={{ color: 'var(--muted)' }}
            >
              ({cat.count})
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
});

// ============================================
// TAGS WIDGET
// ============================================

const TagsWidget = React.memo(({ tags }) => {
  if (!tags?.length) return null;

  const sortedTags = [...tags].sort((a, b) => b.count - a.count).slice(0, 15);

  return (
    <div
      className="p-4 rounded-xl border overflow-hidden"
      style={{ borderColor: 'var(--border)' }}
    >
      <h3
        className="text-sm font-semibold mb-3 flex items-center gap-2"
        style={{ color: 'var(--text)' }}
      >
        <FiTag className="w-4 h-4 text-purple-500" />
        Popular Tags
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {sortedTags.map(tag => (
          <Link
            key={tag.name}
            to={`/blog/tag/${encodeURIComponent(tag.name)}`}
            className="px-2.5 py-0.5 rounded-full text-[10px] transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--border-light)',
              color: 'var(--text)',
            }}
          >
            #{tag.name}
          </Link>
        ))}
      </div>
    </div>
  );
});

// ============================================
// BACK TO TOP BUTTON
// ============================================

const BackToTop = React.memo(() => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-40 p-3 rounded-full shadow-lg transition-all hover:scale-110"
      style={{
        backgroundColor: 'var(--text)',
        color: 'var(--bg)',
      }}
      aria-label="Back to top"
    >
      <FiArrowUp className="w-5 h-5" />
    </button>
  );
});

// ============================================
// RELATED POSTS
// ============================================

const RelatedPosts = React.memo(({ posts }) => {
  if (!posts?.length) return null;

  return (
    <div
      className="mt-8 pt-6 border-t overflow-hidden"
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-6 rounded-full bg-gradient-to-b from-blue-500 to-purple-500" />
        <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
          You Might Also Like
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {posts.map(post => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group block rounded-xl overflow-hidden border transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
            style={{ borderColor: 'var(--border)' }}
          >
            {post.featuredImage && (
              <div className="h-36 overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            )}
            <div className="p-3">
              <div
                className="flex items-center gap-2 text-xs mb-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiClock className="w-3 h-3" />
                <span>{getFormattedReadingTime(post.readingTime || 1)}</span>
              </div>
              <h4
                className="text-sm font-semibold line-clamp-2 group-hover:underline"
                style={{ color: 'var(--text)' }}
              >
                {post.title}
              </h4>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
});

// ============================================
// GOOGLE AD SPACE COMPONENT
// ============================================

const AdSpace = React.memo(({ height = '250px', label = 'Advertisement' }) => {
  return (
    <div
      className="p-3 rounded-xl border border-dashed text-center overflow-hidden"
      style={{ borderColor: 'var(--border)' }}
    >
      <div
        className="flex items-center justify-center gap-2 text-[10px]"
        style={{ color: 'var(--muted)' }}
      >
        <span>📢</span>
        <span>{label}</span>
      </div>
      <div
        className="flex items-center justify-center"
        style={{
          height: height,
          backgroundColor: 'var(--border-light)',
          minHeight: '60px',
        }}
      >
        <span className="text-xs" style={{ color: 'var(--muted)' }}>
          Google Ad Space
        </span>
      </div>
    </div>
  );
});

// ============================================
// MAIN BLOGPOST COMPONENT
// ============================================

const BlogPost = memo(function BlogPost() {
  // =============================================
  // 1. ALL HOOKS - CALLED IN THE SAME ORDER EVERY TIME
  // =============================================

  const { slug } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);

  const renderCount = useRef(0);

  // ✅ REMOVED: metaData - No client-side OG generation
  // Social media scrapers get OG tags from server API

  // Schema markup - Keep for SEO
  const schemaMarkup = useMemo(() => {
    if (!blog) return null;
    const shareUrl = `${window.location.origin}/blog/${blog.slug}`;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: blog.seoTitle || blog.title,
      alternativeHeadline: blog.title,
      description: blog.seoDescription || blog.excerpt,
      image: blog.ogImage || blog.featuredImage || '/images/default-og.jpg',
      datePublished: blog.publishedAt || blog.createdAt,
      dateModified: blog.updatedAt || blog.publishedAt || blog.createdAt,
      author: {
        '@type': 'Person',
        name: blog.author?.name || 'WD Hajbul',
        url: 'https://wdhejbul.com/about',
      },
      publisher: {
        '@type': 'Organization',
        name: 'WD Hajbul Creative Studio',
        url: 'https://wdhejbul.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://wdhejbul.com/logo.png',
          width: 512,
          height: 512,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': shareUrl,
      },
      keywords: blog.seoKeywords?.join(', ') || blog.tags?.join(', ') || '',
      articleSection: blog.category || '',
      wordCount: blog.content?.split(/\s+/).length || 0,
      isAccessibleForFree: true,
      inLanguage: 'en-US',
      about: {
        '@type': 'Thing',
        name: blog.category || 'Design',
      },
    };
  }, [blog]);

  const loadBlog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blogService.getBlogBySlug(slug);
      setBlog(response.blog);

      const relatedRes = await blogService.getRelatedPosts(
        response.blog.id,
        response.blog.category,
        response.blog.tags,
        slug,
      );
      setRelatedPosts(relatedRes.relatedPosts || []);

      const allBlogs = await blogService.getBlogs({ page: 1, limit: 50 });
      const sortedByViews = [...allBlogs.blogs]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .filter(b => b.slug !== slug);
      setPopularPosts(sortedByViews.slice(0, 10));

      const [categoriesRes, tagsRes] = await Promise.all([
        blogService.getCategories(),
        blogService.getTags(),
      ]);
      setCategories(categoriesRes.categories || []);
      setTags(tagsRes.tags || []);
    } catch (err) {
      setError(err.message);
      if (err.message.includes('not found')) {
        setTimeout(() => navigate('/blog', { replace: true }), 3000);
      }
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    loadBlog();
  }, [loadBlog]);

  // =============================================
  // 2. CONDITIONAL RETURNS - AFTER ALL HOOKS
  // =============================================

  renderCount.current += 1;

  if (loading) {
    return <BlogPostSkeleton />;
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen pt-24 pb-20 flex items-center justify-center">
        <div className="text-center">
          <h1
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--text)' }}
          >
            {error || 'Blog post not found'}
          </h1>
          <p className="mb-8" style={{ color: 'var(--muted)' }}>
            Redirecting to blog home...
          </p>
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
            style={{ borderColor: 'var(--text)' }}
          />
        </div>
      </div>
    );
  }

  // =============================================
  // 3. RENDER
  // =============================================

  const shareUrl = `${window.location.origin}/blog/${blog.slug}`;
  const formattedDate = new Date(blog.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const displayReadingTime = getFormattedReadingTime(blog.readingTime || 1);
  const readingTimeColor = getReadingTimeColor(blog.readingTime || 1);
  const metaRobots =
    blog.status === 'published' ? 'index, follow' : 'noindex, follow';

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/blogs?action=social-preview&slug=xxx
      */}
      <Helmet>
        <title>{blog.seoTitle || blog.title}</title>
        <meta
          name="description"
          content={blog.seoDescription || blog.excerpt}
        />
        <meta
          name="keywords"
          content={blog.seoKeywords?.join(', ') || blog.tags?.join(', ') || ''}
        />
        <link rel="canonical" href={shareUrl} />
        <meta name="robots" content={metaRobots} />
        <meta name="author" content={blog.author?.name || 'WD Hajbul'} />
        <meta name="publish-date" content={blog.publishedAt} />

        {/* Schema JSON-LD - Keep for SEO */}
        {schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(schemaMarkup)}
          </script>
        )}
      </Helmet>

      <ReadingProgressBar />
      <BackToTop />

      <div
        className="min-h-screen pt-2 pb-12 overflow-x-hidden"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Sticky Navigation */}
        <div
          className="sticky top-[72px] z-30 backdrop-blur-md border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-2">
            <Link
              to="/blog"
              className="flex items-center gap-2 text-sm transition-all hover:gap-3"
              style={{ color: 'var(--muted)' }}
            >
              <FiArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            <ShareButtons
              title={blog.title}
              url={shareUrl}
              blogId={blog.id}
              blog={blog}
            />
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="max-w-6xl mx-auto px-4 pt-4">
          <Breadcrumb category={blog.category} title={blog.title} />
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 pt-2 pb-12 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 overflow-hidden">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-8 space-y-6 pt-4 overflow-hidden">
              {/* Featured Image */}
              {blog.featuredImage && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  className="relative w-full rounded-2xl overflow-hidden bg-black/5 dark:bg-white/5"
                >
                  <div className="relative w-full pt-[56.25%]">
                    <img
                      src={blog.featuredImage}
                      alt={blog.imageAlt || blog.title}
                      className="absolute top-0 left-0 w-full h-full object-cover"
                      loading="eager"
                    />
                    {blog.imageCaption && (
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                        <p className="text-xs text-white/80 text-center">
                          {blog.imageCaption}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Title & Meta */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <h1
                  className="text-2xl md:text-3xl font-bold mb-3 break-words"
                  style={{ color: 'var(--text)' }}
                >
                  {blog.title}
                </h1>
                <div
                  className="flex flex-wrap gap-3 text-xs"
                  style={{ color: 'var(--muted)' }}
                >
                  <span className="flex items-center gap-1.5">
                    <FiCalendar className="w-3.5 h-3.5" />
                    {formattedDate}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${readingTimeColor}`}
                  >
                    <FiClock className="w-3 h-3" />
                    <span>{displayReadingTime}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FiEye className="w-3.5 h-3.5" />
                    {blog.views?.toLocaleString() || 0} views
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FiFolder className="w-3.5 h-3.5" />
                    <Link
                      to={`/blog/category/${encodeURIComponent(blog.category)}`}
                      className="hover:underline capitalize"
                    >
                      {blog.category}
                    </Link>
                  </span>
                  {blog.tags && blog.tags.length > 0 && (
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <FiTag className="w-3.5 h-3.5" />
                      <div className="flex gap-1 flex-wrap">
                        {blog.tags.slice(0, 3).map(tag => (
                          <Link
                            key={tag}
                            to={`/blog/tag/${encodeURIComponent(tag)}`}
                            className="hover:underline"
                          >
                            #{tag}
                          </Link>
                        ))}
                      </div>
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Excerpt */}
              {blog.excerpt && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  className="p-4 rounded-xl italic"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    {blog.excerpt}
                  </p>
                </motion.div>
              )}

              {/* Table of Contents */}
              {blog.tableOfContents?.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  className="overflow-hidden"
                >
                  <TableOfContents headings={blog.tableOfContents} />
                </motion.div>
              )}

              {/* Content */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="blog-post-content prose prose-sm dark:prose-invert max-w-none overflow-hidden"
                style={{
                  color: 'var(--text)',
                  lineHeight: 1.75,
                }}
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Author Bio */}
              {blog.author && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  className="p-4 rounded-xl border overflow-hidden"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
                      style={{
                        backgroundColor: 'var(--border-light)',
                        color: 'var(--text)',
                      }}
                    >
                      {blog.author.name?.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4
                        className="font-semibold mb-0.5 text-sm"
                        style={{ color: 'var(--text)' }}
                      >
                        {blog.author.name}
                      </h4>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>
                        Designer, developer, and creative problem solver.
                        Passionate about creating beautiful and functional
                        digital experiences.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                  className="overflow-hidden"
                >
                  <RelatedPosts posts={relatedPosts} />
                </motion.div>
              )}

              {/* Comment Section */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="pt-4 border-t overflow-hidden"
                style={{ borderColor: 'var(--border)' }}
              >
                <CommentSection blogId={blog.id} />
              </motion.div>
            </div>

            {/* Right Column - Sticky Sidebar */}
            <div className="lg:col-span-4 overflow-hidden">
              <div className="lg:sticky lg:top-[88px] space-y-6">
                <FiverrCard variant="sidebar" />
                <AdSpace height="250px" label="Advertisement" />
                <PopularPosts posts={popularPosts} />
                <AdSpace height="200px" label="Advertisement" />
                <CategoriesWidget categories={categories} />
                <TagsWidget tags={tags} />
                <AdSpace height="150px" label="Advertisement" />
                <div
                  className="p-4 rounded-xl border text-center overflow-hidden"
                  style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--border-light)',
                  }}
                >
                  <div className="flex items-center justify-center mb-2">
                    <div
                      className="p-2 rounded-full"
                      style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                    >
                      <FiMessageCircle className="w-5 h-5 text-blue-500" />
                    </div>
                  </div>
                  <h3
                    className="text-sm font-semibold mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    Have questions about this post?
                  </h3>
                  <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                    Let's discuss your thoughts or questions about this article.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:gap-3"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <FiMessageCircle className="w-3.5 h-3.5" />
                    Contact Us
                    <FiArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .blog-post-content {
          max-width: 100%;
          overflow-x: hidden;
          word-wrap: break-word;
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
        }
        .blog-post-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.75rem;
          margin: 1.5rem 0;
        }
        .blog-post-content table {
          display: block;
          overflow-x: auto;
          max-width: 100%;
        }
        .blog-post-content pre,
        .blog-post-content code {
          max-width: 100%;
          overflow-x: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .blog-post-content * {
          max-width: 100%;
          user-select: text !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
        }
        .blog-post-content blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: var(--muted);
        }
        .blog-post-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          color: var(--text);
        }
        .blog-post-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          color: var(--text);
        }
        .blog-post-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          color: var(--text);
        }
        .blog-post-content ul,
        .blog-post-content ol {
          margin: 0.75rem 0;
          padding-left: 1.5rem;
        }
        .blog-post-content li {
          margin-bottom: 0.25rem;
          line-height: 1.6;
        }
        .blog-post-content a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .blog-post-content a:hover {
          opacity: 0.8;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
});

export default BlogPost;
